import {yupResolver} from "@hookform/resolvers/yup";
import reverse from "lodash/reverse";
import sortBy from "lodash/sortBy";
import {useState} from "react";
import {FormProvider, useForm} from "react-hook-form";

import {NavTabs} from "@/components/navTabs/NavTabs.tsx";
import {ServiceGroup, ServiceTag} from "@/lib/types/checks.ts";
import {DashboardDefinition} from "@/lib/types/website.ts";
import {
  DashboardFormDefaults,
  DashboardFormFields,
  DashboardFormSchema,
  DashboardFormTabs,
} from "@/pages/dashboard/edit/dashboardForm.ts";
import {DashboardFormCheckPicker} from "@/pages/dashboard/edit/subforms/DashboardFormCheckPicker.tsx";
import {DashboardFormContent} from "@/pages/dashboard/edit/subforms/DashboardFormContent.tsx";
import {DashboardFormGeneral} from "@/pages/dashboard/edit/subforms/DashboardFormGeneral.tsx";

interface DashboardFormProps {
  formId: string;
  dashboardDef: DashboardDefinition;
  dashboardDefs: DashboardDefinition[];
  tags: ServiceTag[];
  monitoringServiceTypes: ServiceGroup[];
  updateDashboardDef: (dashboardDef: DashboardDefinition) => void;
  hide: () => void;
  showCheckPicker: boolean;
  onShowCheckPickerChange: (show: boolean) => void;
}

export const DashboardForm = ({
  formId,
  dashboardDef,
  dashboardDefs,
  tags,
  monitoringServiceTypes,
  updateDashboardDef,
  hide,
  showCheckPicker,
  onShowCheckPickerChange,
}: DashboardFormProps) => {
  const [tab, setTab] = useState(DashboardFormTabs.GENERAL);

  const dashboardPosition = dashboardDef.id
    ? reverse(sortBy(dashboardDefs, (d) => d.ordering)).findIndex((dashboard) => dashboard.id === dashboardDef.id)
    : dashboardDefs.length;

  const updatedDashboardDef = {...dashboardDef, ...{dashboard_position: dashboardPosition}};

  const methods = useForm<DashboardFormFields>({
    resolver: yupResolver<DashboardFormFields>(DashboardFormSchema),
    defaultValues: (updatedDashboardDef as DashboardFormFields) || DashboardFormDefaults,
  });

  return (
    <FormProvider {...methods}>
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();

          methods.handleSubmit(() => {
            updateDashboardDef(methods.getValues() as DashboardDefinition);
            hide();
          })(e);
        }}
        noValidate
      >
        {showCheckPicker ? (
          <DashboardFormCheckPicker tags={tags} monitoringServiceTypes={monitoringServiceTypes} />
        ) : (
          <>
            <NavTabs
              className="mb-4"
              items={[
                {
                  id: DashboardFormTabs.GENERAL,
                  title: "General",
                },
                {
                  id: DashboardFormTabs.CONTENT,
                  title: "Display Settings",
                },
              ]}
              selectTab={(tab) => setTab(tab as DashboardFormTabs)}
              selectedId={tab}
            />
            {tab === DashboardFormTabs.GENERAL && <DashboardFormGeneral dashboardDefs={dashboardDefs} />}
            {tab === DashboardFormTabs.CONTENT && (
              <DashboardFormContent tags={tags} onOpenCheckPicker={() => onShowCheckPickerChange(true)} />
            )}
          </>
        )}
      </form>
    </FormProvider>
  );
};
